import { z } from 'zod';

import { FixedIdAssetSnapshotSchema } from '../holding-units/fixed-id-assets.schema.js';
import { HoldingUnitEventEntityTypeSchema } from '../holding-units/holding-unit-event-envelope.schema.js';
import {
  HoldingUnitLoadSessionItemSelectionMethodSchema,
  HoldingUnitLoadSessionMeasuredDiscrepancyActionSchema,
  HoldingUnitLoadSessionStatusSchema,
  HoldingUnitMeasuredDiscrepancyCommittedWeightEventTypeSchema,
  HoldingUnitReservationStateSchema,
  HoldingUnitSnapshotSchema,
} from '../holding-units/holding-units.schema.js';
import type {
  HoldingUnitsCanonicalAggregateState,
  HoldingUnitsCanonicalFixedIdAssetState,
  HoldingUnitsCanonicalHoldingUnitState,
  HoldingUnitsCanonicalLatestShippedFact,
  HoldingUnitsCanonicalLatestWeightFact,
  HoldingUnitsCanonicalLoadSessionHead,
  HoldingUnitsCanonicalLoadSessionItemState,
  HoldingUnitsCanonicalLoadSessionMaterialGroupState,
  HoldingUnitsCanonicalLoadSessionState,
  HoldingUnitsCanonicalSnapshotRecord,
  HoldingUnitsCanonicalStreamSnapshot,
} from './holding-units-canonical-snapshot.dto.js';
import {
  HOLDING_UNITS_CANONICAL_STREAM_SNAPSHOT_SCHEMA_HASH,
  HOLDING_UNITS_CANONICAL_STREAM_SNAPSHOT_SCHEMA_VERSION,
} from './holding-units-canonical-snapshot.dto.js';

const MAX_SIGNED_BIGINT = 9_223_372_036_854_775_807n;
const TimestampSchema = z.iso.datetime();

export const HoldingUnitsCanonicalOuterSequenceSchema = z
  .string()
  .regex(/^(0|[1-9]\d*)$/, 'Expected a decimal sequence.')
  .refine((value) => BigInt(value) <= MAX_SIGNED_BIGINT, 'Sequence exceeds signed 64-bit range.');

export const HoldingUnitsCanonicalLatestWeightFactSchema: z.ZodType<HoldingUnitsCanonicalLatestWeightFact> =
  z
    .object({
      eventId: z.uuid(),
      streamVersion: z.number().int().positive(),
      eventType: z.enum(['holdingUnitWeightAdded', 'holdingUnitWeightCorrected']),
      occurredAt: TimestampSchema,
      actorUserId: z.uuid(),
      grossLb: z.number().nonnegative().nullable(),
      tareLb: z.number().nonnegative().nullable(),
      netLb: z.number().nonnegative(),
    })
    .strict();

export const HoldingUnitsCanonicalLatestShippedFactSchema: z.ZodType<HoldingUnitsCanonicalLatestShippedFact> =
  z
    .object({
      eventId: z.uuid(),
      streamVersion: z.number().int().positive(),
      occurredAt: TimestampSchema,
      shipmentId: z.uuid().nullable(),
      coverShipmentId: z.uuid().nullable(),
    })
    .strict()
    .superRefine((value, ctx) => {
      if ((value.shipmentId ? 1 : 0) + (value.coverShipmentId ? 1 : 0) !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'A shipped fact requires exactly one shipment target.',
          path: ['shipmentId'],
        });
      }
    });

export const HoldingUnitsCanonicalHoldingUnitStateSchema: z.ZodType<HoldingUnitsCanonicalHoldingUnitState> =
  z
    .object({
      kind: z.literal('holdingUnit'),
      snapshot: HoldingUnitSnapshotSchema,
      reservation: HoldingUnitReservationStateSchema.nullable(),
      latestWeight: HoldingUnitsCanonicalLatestWeightFactSchema.nullable(),
      latestShipped: HoldingUnitsCanonicalLatestShippedFactSchema.nullable(),
    })
    .strict()
    .superRefine((value, ctx) => {
      if (
        value.reservation &&
        (value.reservation.companyId !== value.snapshot.companyId ||
          value.reservation.unitId !== value.snapshot.id)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Reservation state must belong to the snapshotted holding unit.',
          path: ['reservation'],
        });
      }
    });

export const HoldingUnitsCanonicalFixedIdAssetStateSchema: z.ZodType<HoldingUnitsCanonicalFixedIdAssetState> =
  z
    .object({
      kind: z.literal('fixedIdAsset'),
      snapshot: FixedIdAssetSnapshotSchema,
    })
    .strict();

export const HoldingUnitsCanonicalLoadSessionHeadSchema: z.ZodType<HoldingUnitsCanonicalLoadSessionHead> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      stationId: z.uuid(),
      status: HoldingUnitLoadSessionStatusSchema,
      sessionName: z.string().trim().min(1).max(80).nullable(),
      toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
      createdByUserId: z.uuid(),
      createdAt: TimestampSchema,
      updatedAt: TimestampSchema,
      version: z.number().int().positive(),
      lastEventId: z.uuid(),
    })
    .strict();

export const HoldingUnitsCanonicalLoadSessionMaterialGroupStateSchema: z.ZodType<HoldingUnitsCanonicalLoadSessionMaterialGroupState> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      loadSessionId: z.uuid(),
      materialId: z.uuid(),
      autoPickEnabled: z.boolean(),
      toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
      yardLocationIds: z.array(z.uuid()).nullable(),
      createdAt: TimestampSchema,
      updatedAt: TimestampSchema,
      createdByUserId: z.uuid().nullable(),
      streamVersion: z.number().int().positive(),
    })
    .strict();

export const HoldingUnitsCanonicalLoadSessionItemStateSchema: z.ZodType<HoldingUnitsCanonicalLoadSessionItemState> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      loadSessionId: z.uuid(),
      groupId: z.uuid(),
      unitId: z.uuid(),
      selectionMethod: HoldingUnitLoadSessionItemSelectionMethodSchema,
      snapshotNetLbAtSelection: z.number().nonnegative(),
      snapshotGrossLbAtSelection: z.number().nonnegative().nullable(),
      snapshotTareLbAtSelection: z.number().nonnegative().nullable(),
      loadMeasuredGrossLb: z.number().nonnegative().nullable(),
      loadMeasuredNetLb: z.number().nonnegative().nullable(),
      deltaLossLb: z.number().nullable(),
      toleranceProfileKey: z.string().trim().min(1).max(50).nullable(),
      capturedAt: TimestampSchema,
      capturedByUserId: z.uuid().nullable(),
      wasAutoPicked: z.boolean(),
      measuredDiscrepancyAction: HoldingUnitLoadSessionMeasuredDiscrepancyActionSchema.nullable(),
      measuredDiscrepancyResolvedAt: TimestampSchema.nullable(),
      measuredDiscrepancyResolvedByUserId: z.uuid().nullable(),
      measuredDiscrepancyCommittedWeightEventId: z.uuid().nullable(),
      measuredDiscrepancyCommittedWeightEventType:
        HoldingUnitMeasuredDiscrepancyCommittedWeightEventTypeSchema.nullable(),
      removedAt: TimestampSchema.nullable(),
      removedByUserId: z.uuid().nullable(),
      streamVersion: z.number().int().positive(),
    })
    .strict();

export const HoldingUnitsCanonicalLoadSessionStateSchema: z.ZodType<HoldingUnitsCanonicalLoadSessionState> =
  z
    .object({
      kind: z.literal('loadSession'),
      session: HoldingUnitsCanonicalLoadSessionHeadSchema,
      groups: z.array(HoldingUnitsCanonicalLoadSessionMaterialGroupStateSchema),
      items: z.array(HoldingUnitsCanonicalLoadSessionItemStateSchema),
    })
    .strict()
    .superRefine((value, ctx) => {
      validateOrderedLoadSessionState(value, ctx);
    });

export const HoldingUnitsCanonicalAggregateStateSchema: z.ZodType<HoldingUnitsCanonicalAggregateState> =
  z.union([
    HoldingUnitsCanonicalHoldingUnitStateSchema,
    HoldingUnitsCanonicalFixedIdAssetStateSchema,
    HoldingUnitsCanonicalLoadSessionStateSchema,
  ]);

export const HoldingUnitsCanonicalStreamSnapshotSchema: z.ZodType<HoldingUnitsCanonicalStreamSnapshot> =
  z
    .object({
      entityType: HoldingUnitEventEntityTypeSchema,
      entityId: z.uuid(),
      baseOuterSequence: HoldingUnitsCanonicalOuterSequenceSchema,
      baseStreamVersion: z.number().int().positive(),
      lastEventId: z.uuid(),
      createdAt: TimestampSchema,
      snapshotSchemaVersion: z.literal(HOLDING_UNITS_CANONICAL_STREAM_SNAPSHOT_SCHEMA_VERSION),
      snapshotSchemaHash: z.literal(HOLDING_UNITS_CANONICAL_STREAM_SNAPSHOT_SCHEMA_HASH),
      state: HoldingUnitsCanonicalAggregateStateSchema,
    })
    .strict()
    .superRefine((value, ctx) => {
      validateSnapshotEnvelope(value, ctx);
    });

export const HoldingUnitsCanonicalSnapshotRecordSchema: z.ZodType<HoldingUnitsCanonicalSnapshotRecord> =
  z
    .object({
      kind: z.literal('snapshot'),
      headStreamVersion: z.number().int().positive(),
      snapshot: HoldingUnitsCanonicalStreamSnapshotSchema,
    })
    .strict()
    .superRefine((value, ctx) => {
      if (value.headStreamVersion < value.snapshot.baseStreamVersion) {
        ctx.addIssue({
          code: 'custom',
          message: 'Snapshot transfer head cannot precede its immutable base.',
          path: ['headStreamVersion'],
        });
      }
    });

function validateSnapshotEnvelope(
  value: HoldingUnitsCanonicalStreamSnapshot,
  ctx: z.RefinementCtx,
): void {
  if (value.state.kind !== value.entityType) {
    ctx.addIssue({
      code: 'custom',
      message: 'Snapshot state kind must match entityType.',
      path: ['state', 'kind'],
    });
    return;
  }

  const aggregate = value.state.kind === 'loadSession' ? value.state.session : value.state.snapshot;
  if (
    aggregate.id !== value.entityId ||
    aggregate.version !== value.baseStreamVersion ||
    aggregate.createdAt !== value.createdAt
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Snapshot envelope must match aggregate identity, base version, and createdAt.',
      path: ['state'],
    });
  }

  const aggregateLastEventId =
    value.state.kind === 'holdingUnit'
      ? value.state.snapshot.lastEventId
      : value.state.kind === 'loadSession'
        ? value.state.session.lastEventId
        : null;
  if (aggregateLastEventId !== null && aggregateLastEventId !== value.lastEventId) {
    ctx.addIssue({
      code: 'custom',
      message: 'Snapshot lastEventId must match the aggregate head.',
      path: ['lastEventId'],
    });
  }

  if (value.state.kind === 'holdingUnit') {
    for (const [key, fact] of [
      ['latestWeight', value.state.latestWeight],
      ['latestShipped', value.state.latestShipped],
    ] as const) {
      if (fact && fact.streamVersion > value.baseStreamVersion) {
        ctx.addIssue({
          code: 'custom',
          message: 'Snapshot fact cannot exceed the base stream version.',
          path: ['state', key, 'streamVersion'],
        });
      }
    }
  }

  if (value.state.kind === 'loadSession') {
    for (const [collectionName, entries] of [
      ['groups', value.state.groups],
      ['items', value.state.items],
    ] as const) {
      for (const [index, entry] of entries.entries()) {
        if (entry.streamVersion > value.baseStreamVersion) {
          ctx.addIssue({
            code: 'custom',
            message: 'Load-session child state cannot exceed the base stream version.',
            path: ['state', collectionName, index, 'streamVersion'],
          });
        }
      }
    }
  }
}

function validateOrderedLoadSessionState(
  value: HoldingUnitsCanonicalLoadSessionState,
  ctx: z.RefinementCtx,
): void {
  const groupIds = new Set<string>();
  for (const [index, group] of value.groups.entries()) {
    if (
      group.companyId !== value.session.companyId ||
      group.storeId !== value.session.storeId ||
      group.locationId !== value.session.locationId ||
      group.loadSessionId !== value.session.id
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Material-group state must belong to the snapshotted load session.',
        path: ['groups', index],
      });
    }
    if (groupIds.has(group.id) || (index > 0 && value.groups[index - 1].id >= group.id)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Material groups must be unique and strictly ordered by ID.',
        path: ['groups', index, 'id'],
      });
    }
    groupIds.add(group.id);
  }

  const itemIds = new Set<string>();
  for (const [index, item] of value.items.entries()) {
    if (
      item.companyId !== value.session.companyId ||
      item.storeId !== value.session.storeId ||
      item.locationId !== value.session.locationId ||
      item.loadSessionId !== value.session.id ||
      !groupIds.has(item.groupId)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Item state must belong to a material group in the snapshotted load session.',
        path: ['items', index],
      });
    }
    if (itemIds.has(item.id) || (index > 0 && value.items[index - 1].id >= item.id)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Load-session items must be unique and strictly ordered by ID.',
        path: ['items', index, 'id'],
      });
    }
    itemIds.add(item.id);
  }
}
